import { useState } from 'react'
import { Search, AlertTriangle, ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ExportButtons from '../../components/ExportButtons'
import i18n from '../../i18n'
import { localizeApiDetail } from '../../i18n/apiError'
import { formatNorwegianDate } from '../../utils/dateUtils'
import KvoteBadge from './KvoteBadge'
import { personOppslag, type Kvote, type PersonResponse } from './oppslagApi'
import type { OppslagKey } from './detaljFelter'

const AAR_VALG = [0, 1, 2, 5, 10]

/** dd.MM.yyyy, as everywhere else in the app. */
const dato = (v: unknown) => (v ? formatNorwegianDate(String(v)) : '—')

const PERSON_KOLONNER: { key: string; labelKey: OppslagKey; format?: (v: unknown) => string }[] = [
  { key: 'dato', labelKey: 'kolonner.dato', format: dato },
  { key: 'kjennemerke', labelKey: 'kolonner.kjennemerke' },
  { key: 'merke', labelKey: 'kolonner.merke' },
  { key: 'handelsbetegnelse', labelKey: 'kolonner.modell' },
  { key: 'understellsnummer', labelKey: 'kolonner.understellsnummer' },
  { key: 'registrert_pa_eier', labelKey: 'kolonner.registrertPaEier', format: dato },
]

/**
 * One row per vehicle per snapshot date.
 *
 * A date at which the person owned nothing is kept as a row saying so. That
 * absence is a finding — dropping it would make a gap in the timeline
 * indistinguishable from a date that was never looked up.
 *
 * No fødselsnummer goes into the export. The file is titled by surname only,
 * because exported files get mailed around and the number does not need to
 * travel with them.
 */
function tilRader(svar: PersonResponse) {
  return svar.snapshots.flatMap((s) =>
    s.kjoretoy.length === 0
      ? [{ dato: s.dato, kjennemerke: i18n.t('person.noVehicles', { ns: 'oppslag' }) }]
      : s.kjoretoy.map((k) => ({
          dato: s.dato,
          kjennemerke: k.kjennemerke ?? '',
          merke: k.merke ?? '',
          handelsbetegnelse: k.handelsbetegnelse ?? '',
          understellsnummer: k.understellsnummer ?? '',
          registrert_pa_eier: k.eier?.registrert_pa_eier ?? '',
        })),
  )
}

export default function PersonTab() {
  const { t } = useTranslation('oppslag')
  const [fnr, setFnr] = useState('')
  const [etternavn, setEtternavn] = useState('')
  const [aar, setAar] = useState(5)
  const [laster, setLaster] = useState(false)
  const [svar, setSvar] = useState<PersonResponse | null>(null)
  const [kvote, setKvote] = useState<Kvote | null>(null)
  const [feil, setFeil] = useState<string | null>(null)

  // Mirrors the backend's validators so the user is told before a quota unit
  // is spent. The backend still enforces both — this is convenience, not trust.
  const fnrOk = /^\d{11}$/.test(fnr.trim())
  const etternavnOk = etternavn.trim().length > 0 && !etternavn.trim().includes(' ')
  const kanSoke = fnrOk && etternavnOk && !laster

  async function utfor() {
    if (!kanSoke) return
    setLaster(true)
    setFeil(null)
    try {
      const res = await personOppslag(fnr.trim(), etternavn.trim(), aar)
      setSvar(res)
      setKvote(res.kvote)
    } catch (e: any) {
      const d = e?.response?.data?.detail
      setFeil(
        typeof d === 'string'
          ? (localizeApiDetail(d) ?? t('person.lookupFailed'))
          : (localizeApiDetail(d?.melding) ?? t('person.lookupFailed')),
      )
      setSvar(null)
    } finally {
      setLaster(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Stated plainly, not buried. A national-ID lookup is logged and
          auditable, and the person running it should know that before they
          run it — not discover it later. */}
      <div className="card p-3 flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20">
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          {t('person.warning')}
        </span>
      </div>

      <div className="card p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('person.title')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('person.intro')}
            </p>
          </div>
          <KvoteBadge kvote={kvote} api="maskinporten" />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('person.fnrLabel')}
            </span>
            <input
              data-testid="person-fnr"
              value={fnr}
              onChange={(e) => setFnr(e.target.value)}
              inputMode="numeric"
              autoComplete="off"
              className="rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm font-mono w-44"
            />
          </label>

          <label className="text-sm">
            <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('person.etternavnLabel')}
            </span>
            <input
              data-testid="person-etternavn"
              value={etternavn}
              onChange={(e) => setEtternavn(e.target.value)}
              autoComplete="off"
              className="rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm w-48"
            />
          </label>

          <label className="text-sm">
            <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('person.aarLabel')}
            </span>
            <select
              data-testid="person-aar"
              value={aar}
              onChange={(e) => setAar(Number(e.target.value))}
              className="rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
            >
              {AAR_VALG.map((a) => (
                <option key={a} value={a}>
                  {a === 0 ? t('person.onlyToday') : t('person.years', { n: a })}
                </option>
              ))}
            </select>
          </label>

          <button
            data-testid="person-sok"
            onClick={utfor}
            disabled={!kanSoke}
            className="btn-primary text-sm disabled:opacity-40 flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {laster ? t('felles.searching') : t('felles.search')}
          </button>
        </div>

        {/* Cost cannot be known in advance: SVV bills per vehicle returned,
            per snapshot. Say that rather than print a number that will be wrong. */}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('person.cost', { n: aar * 2 + 1 })}
        </p>

        {fnr && !fnrOk && (
          <p data-testid="person-fnr-feil" className="text-sm text-red-600 dark:text-red-400">
            {t('person.fnrInvalid')}
          </p>
        )}
        {etternavn && !etternavnOk && (
          <p
            data-testid="person-etternavn-feil"
            className="text-sm text-red-600 dark:text-red-400"
          >
            {t('person.etternavnInvalid')}
          </p>
        )}
      </div>

      {feil && (
        <div
          data-testid="person-feil"
          className="card p-3 flex items-start gap-2 text-sm text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{feil}</span>
        </div>
      )}

      {svar && (
        <div className="card p-4 space-y-3" data-testid="person-resultat">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {t('person.resultHeading', { etternavn: svar.etternavn, n: svar.snapshots.length })}
            </h3>
            <ExportButtons
              data={tilRader(svar)}
              columns={PERSON_KOLONNER.map(({ labelKey, ...k }) => ({ ...k, label: t(labelKey) }))}
              metadata={{
                title: t('person.exportTitle', { etternavn: svar.etternavn }),
                // An export cut short by quota must say so, or the recipient
                // reads a partial history as a complete one.
                filters: svar.stoppet_pga_kvote
                  ? [t('person.exportIncomplete')]
                  : undefined,
              }}
            />
          </div>

          {svar.stoppet_pga_kvote && (
            <p
              data-testid="person-kvote-stopp"
              className="text-sm text-amber-700 dark:text-amber-300"
            >
              {t('person.quotaStopped')}
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th>{t('kolonner.dato')}</th>
                  <th>{t('kolonner.kjennemerke')}</th>
                  <th>{t('kolonner.merke')}</th>
                  <th>{t('kolonner.modell')}</th>
                  <th>{t('kolonner.registrertPaEier')}</th>
                </tr>
              </thead>
              <tbody>
                {svar.snapshots.map((s) =>
                  s.kjoretoy.length === 0 ? (
                    <tr key={s.dato}>
                      <td>{dato(s.dato)}</td>
                      <td colSpan={4} className="text-gray-500 dark:text-gray-400">
                        {t('person.noVehicles')}
                      </td>
                    </tr>
                  ) : (
                    s.kjoretoy.map((k, i) => (
                      <tr key={`${s.dato}-${k.kjennemerke}-${i}`}>
                        <td>{i === 0 ? dato(s.dato) : ''}</td>
                        <td>{k.kjennemerke}</td>
                        <td>{k.merke ?? '—'}</td>
                        <td>{k.handelsbetegnelse ?? '—'}</td>
                        <td>{dato(k.eier?.registrert_pa_eier)}</td>
                      </tr>
                    ))
                  ),
                )}
              </tbody>
            </table>
          </div>

          {!!svar.feil.length && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('person.svvMessages', { koder: svar.feil.map((f) => f.kode).join(', ') })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
